import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { useEffect, useRef } from "react";

/**
 * CLE-2885 — the basic-mode file picker the rate tools share, with a remove (×) button.
 *
 * PrimeReact 8's basic `FileUpload` has no way to drop a chosen file: once one is selected,
 * a click calls `upload()` (our no-op `uploadHandler`) instead of opening the chooser, and
 * its `<input>` is no longer rendered. So a wrong file could be neither removed nor
 * replaced. The × asks the parent to drop the file; clearing the widget then renders a
 * fresh `<input>` — the chooser opens again and the same file can be picked again.
 *
 * The parent owns the `File`; this component owns the widget and clears it only when the
 * parent's `file` becomes `null` — after the ×, or when a parent resets its form after a
 * run — so the widget never shows a state the parent doesn't have.
 */

const ACCEPT =
  ".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

interface IToolFilePickerProps {
  file: File | null;
  onChange: (file: File | null) => void;
  /** Button label while no file is chosen; the file's name replaces it once one is. */
  chooseLabel: string;
  disabled?: boolean;
}

export function ToolFilePicker({
  file,
  onChange,
  chooseLabel,
  disabled = false,
}: IToolFilePickerProps) {
  const uploadRef = useRef<FileUpload>(null);

  useEffect(() => {
    if (!file) uploadRef.current?.clear();
  }, [file]);

  const remove = () => onChange(null);

  return (
    <div className="flex align-items-center gap-1">
      <FileUpload
        ref={uploadRef}
        mode="basic"
        accept={ACCEPT}
        chooseLabel={file ? file.name : chooseLabel}
        customUpload
        uploadHandler={() => undefined}
        onSelect={(e) => onChange(e.files[0] ?? null)}
        disabled={disabled}
      />
      {file && (
        <Button
          type="button"
          icon="pi pi-times"
          className="p-button-rounded p-button-text p-button-danger p-button-sm"
          aria-label="Remove file"
          tooltip="Remove file"
          tooltipOptions={{ position: "top" }}
          onClick={remove}
          disabled={disabled}
        />
      )}
    </div>
  );
}

export default ToolFilePicker;
